import React, { Component } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import { writeTargets, isWritable } from '../../../util/marineClassOptions';
import { numOrNull, textOrNull } from '../../../util/marineFieldCoercion';
import {
  getBoatMakesForYear,
  getBoatModels,
  createBoatMake,
  createBoatModel,
  SIZE_TIERS,
} from '../../../services/marineReferenceService';

const CURRENT_YEAR = new Date().getFullYear();
const EARLIEST_YEAR = 1960;

/** A server 409 on the natural key, however it's worded — surfaced to a human, not verbatim. */
const DUPLICATE_HINT = /duplicate|already exists|conflict/i;

/**
 * `/vehicles` -> Water's Add-Vehicle-modal equivalent: `renderAddVehicleModal`
 * in `newVehicles.jsx` (~:1168). Same Year -> Make -> Model cascade over
 * `<datalist>` typeahead inputs that also accept free text, with the same
 * `(New Make)` / `(New Model)` hint when what's typed has no match. Class
 * stands where body style stands, except class has no "pick one from the
 * feed" fallback: it is ALWAYS a hand-picked canonical slug, because a new
 * craft has no existing value to read one from.
 *
 * Dimensions (LOA, beam, freeboard) are optional on purpose — a craft can be
 * added before anyone measures it. They stay as raw typed strings in state
 * and are only coerced through `numOrNull` once, at submit — unlike the edit
 * modal's dirty-diff map, there is no round-trip-through-the-coerced-value
 * problem here to work around.
 */
export default class AddCraftModal extends Component {
  state = {
    year: CURRENT_YEAR,
    make: '',
    makeId: null, // set only when `make` exactly matches an entry in availableMakes
    model: '',
    boatClass: '',
    sizeTier: '', // optional — omitted from the create body when blank
    loaFt: '',
    beamFt: '',
    hullHeightIn: '',
    hullCode: '',
    availableMakes: [],
    availableModels: [],
    loadingMakes: false,
    loadingModels: false,
    saving: false,
    error: '',
    errors: {},
  };

  componentDidMount() {
    this.loadMakesForYear(this.state.year);
  }

  loadMakesForYear = async (year) => {
    this.setState({ loadingMakes: true });
    try {
      const availableMakes = await getBoatMakesForYear(year);
      this.setState({ availableMakes, loadingMakes: false });
    } catch (error) {
      this.setState({ loadingMakes: false, error: error.message });
    }
  };

  loadModelsForMake = async (makeId, year) => {
    this.setState({ loadingModels: true });
    try {
      const availableModels = await getBoatModels({ makeId, year });
      this.setState({ availableModels, loadingModels: false });
    } catch (error) {
      this.setState({ loadingModels: false, error: error.message });
    }
  };

  handleYearChange = (value) => {
    const year = Number(value);
    // A year change invalidates both the make match and every downstream
    // model suggestion — the cascade restarts from the top, same as the
    // vehicle screen's loadMakesForYear.
    this.setState({
      year,
      make: '',
      makeId: null,
      model: '',
      availableModels: [],
      errors: { ...this.state.errors, year: '' },
    });
    this.loadMakesForYear(year);
  };

  handleMakeChange = (value) => {
    const match = this.state.availableMakes.find(
      (m) => String(m.make_name).toLowerCase() === value.toLowerCase()
    );
    this.setState({
      make: value,
      makeId: match ? match.make_id : null,
      model: '',
      errors: { ...this.state.errors, make: '' },
    });
    if (match) {
      this.loadModelsForMake(match.make_id, this.state.year);
    } else {
      // A make with no id can't drive `getBoatModels` — it requires exactly
      // one of makeId/make, and passing free-typed text as `make` risks a
      // partial-name match against the wrong maker. A brand new make has no
      // existing models to suggest anyway.
      this.setState({ availableModels: [] });
    }
  };

  handleModelChange = (value) => {
    this.setState({ model: value, errors: { ...this.state.errors, model: '' } });
  };

  handleField = (field, value) => this.setState({ [field]: value });

  /**
   * Resolves the id to send `createBoatModel`. If `make` is an existing
   * match this never runs — `makeId` is already known. Otherwise: create
   * it, then read the id back defensively (`marineReferenceService.js`
   * documents the year-scoped cascade's shape as `make_id`, but does not
   * document `createBoatMake`'s response shape, and the sibling `getBoatMakes`
   * endpoint on the same table uses the bare `id` — so accept either).
   *
   * If the create fails — most likely a 409 on `boat_make`'s unique name
   * index, e.g. two admins typing the same new make — that must not abort
   * the add outright. Re-run the SAME lookup the Make field already uses
   * (the only make-lookup this modal has) and reuse that id if the name now
   * resolves. This can still miss a make that exists but has no model
   * generation covering THIS year (`getBoatMakesForYear`'s own scoping), in
   * which case the original create error is thrown — an honest "could not
   * create" beats a wrong "not found".
   */
  resolveMakeId = async (makeName, year) => {
    let createError = null;
    try {
      const created = await createBoatMake(makeName);
      const id = created && (created.make_id ?? created.id);
      if (id) return id;
      createError = new Error(`"${makeName}" was created but returned no id.`);
    } catch (error) {
      createError = error;
    }

    try {
      const makes = await getBoatMakesForYear(year);
      this.setState({ availableMakes: makes });
      const hit = makes.find((m) => String(m.make_name).toLowerCase() === makeName.toLowerCase());
      if (hit) return hit.make_id;
    } catch (searchError) {
      // ignore — fall through to the original create error below
    }

    throw createError;
  };

  validate = () => {
    const { year, make, model, boatClass } = this.state;
    const errors = {};
    if (!year || year < 1900 || year > CURRENT_YEAR + 2) {
      errors.year = 'Please enter a valid year.';
    }
    if (!make.trim()) errors.make = 'Make is required.';
    if (!model.trim()) errors.model = 'Model is required.';
    if (!boatClass) errors.boatClass = 'Class is required.';
    // Defensive only — the select below is built exclusively from
    // `writeTargets`, so this can't fire from a normal click, only if
    // `boatClass` were ever staged some other way.
    else if (!isWritable(this.props.classes, boatClass)) {
      errors.boatClass = `"${boatClass}" is not a class that can be saved. Pick another.`;
    }
    return errors;
  };

  handleSubmit = async () => {
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      const firstError = errors.year || errors.make || errors.model || errors.boatClass;
      this.setState({ errors, error: firstError });
      return;
    }

    this.setState({ saving: true, error: '', errors: {} });
    const {
      year, make, model, boatClass, sizeTier, loaFt, beamFt, hullHeightIn, hullCode, makeId,
    } = this.state;
    const trimmedMake = make.trim();
    const trimmedModel = model.trim();

    // `!= null`, not `||` — an AUTO_INCREMENT id of 0 is unlikely but not
    // impossible, and truthiness would wrongly re-resolve an already-known
    // existing make.
    let resolvedMakeId;
    try {
      resolvedMakeId = makeId != null ? makeId : await this.resolveMakeId(trimmedMake, year);
    } catch (error) {
      // A collision here is on the MAKE name, not the craft — no
      // `createBoatModel` call has happened yet. Surfacing the friendly
      // "craft already exists" wording would send the admin hunting for a
      // duplicate craft that was never attempted, so this catch is kept
      // separate from createBoatModel's below and never runs it through
      // DUPLICATE_HINT.
      this.setState({ saving: false, error: error.message });
      return;
    }

    try {
      // Model names contain `/` and `(*)` (e.g. `H190B/BS(*)`) — this is a
      // request BODY field, never a path or query segment, so no encoding
      // is needed here (unlike a lookup by name, which this flow never does).
      await createBoatModel({
        make_id: resolvedMakeId,
        model_name: trimmedModel,
        year_start: year,
        year_end: year,
        boat_class: boatClass,
        loa_ft: numOrNull(loaFt),
        beam_ft: numOrNull(beamFt),
        hull_height_in: numOrNull(hullHeightIn),
        hull_code: textOrNull(hullCode),
        // Only when chosen: a blank means "not tiered", which is the server's
        // default, so leaving the key out is the same write with less surface.
        ...(sizeTier ? { size_tier: sizeTier } : {}),
      });

      this.props.onSaved();
    } catch (error) {
      const message = DUPLICATE_HINT.test(error.message) ? 'That craft already exists.' : error.message;
      this.setState({ saving: false, error: message });
    }
  };

  render() {
    const { classes, onClose } = this.props;
    const {
      year, make, model, boatClass, sizeTier, loaFt, beamFt, hullHeightIn, hullCode,
      availableMakes, availableModels, loadingMakes, loadingModels,
      saving, error, errors,
    } = this.state;

    const years = [];
    for (let y = CURRENT_YEAR + 2; y >= EARLIEST_YEAR; y--) years.push(y);

    const isNewMake = make.trim() && !availableMakes.some(
      (m) => String(m.make_name).toLowerCase() === make.trim().toLowerCase()
    );
    const modelNames = [...new Set(availableModels.map((m) => m.model_name))];
    const isNewModel = model.trim() && !modelNames.some(
      (name) => String(name).toLowerCase() === model.trim().toLowerCase()
    );

    return (
      <Modal show onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Craft</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}

          <Form>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Year <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="select"
                    value={year}
                    onChange={(e) => this.handleYearChange(e.target.value)}
                    isInvalid={!!errors.year}
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">{errors.year}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>
                    Make <span className="text-danger">*</span>
                    {isNewMake && <small className="text-success ml-2">(New Make)</small>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type to search or add new make..."
                    value={make}
                    onChange={(e) => this.handleMakeChange(e.target.value)}
                    isInvalid={!!errors.make}
                    disabled={loadingMakes}
                    list="craft-make-suggestions"
                  />
                  <datalist id="craft-make-suggestions">
                    {availableMakes.map((m) => <option key={m.make_id} value={m.make_name} />)}
                  </datalist>
                  {loadingMakes && <small className="text-muted">Loading makes...</small>}
                  <Form.Control.Feedback type="invalid">{errors.make}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Model <span className="text-danger">*</span>
                    {isNewModel && <small className="text-success ml-2">(New Model)</small>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type to search or add new model..."
                    value={model}
                    onChange={(e) => this.handleModelChange(e.target.value)}
                    isInvalid={!!errors.model}
                    disabled={!make.trim() || loadingModels}
                    list="craft-model-suggestions"
                  />
                  <datalist id="craft-model-suggestions">
                    {modelNames.map((name) => <option key={name} value={name} />)}
                  </datalist>
                  {loadingModels && <small className="text-muted">Loading existing models...</small>}
                  <Form.Control.Feedback type="invalid">{errors.model}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Class <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="select"
                    value={boatClass}
                    onChange={(e) => this.handleField('boatClass', e.target.value)}
                    isInvalid={!!errors.boatClass}
                  >
                    <option value="">Select Class...</option>
                    {writeTargets(classes).map((c) => (
                      <option key={c.slug} value={c.slug}>{c.label}</option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">{errors.boatClass}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Size Tier</Form.Label>
                  <Form.Control
                    as="select"
                    value={sizeTier}
                    onChange={(e) => this.handleField('sizeTier', e.target.value)}
                  >
                    <option value="">&mdash;</option>
                    {SIZE_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Form.Control>
                  <small className="text-muted">Optional.</small>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>LOA (ft)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={loaFt}
                    onChange={(e) => this.handleField('loaFt', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Beam (ft)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={beamFt}
                    onChange={(e) => this.handleField('beamFt', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Freeboard (in)</Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    value={hullHeightIn}
                    onChange={(e) => this.handleField('hullHeightIn', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Hull Material</Form.Label>
                  <Form.Control
                    type="text"
                    value={hullCode}
                    onChange={(e) => this.handleField('hullCode', e.target.value)}
                  />
                  <small className="text-muted">Optional — can be filled in later.</small>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <button className="jv-btn jv-btn-tertiary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="jv-btn jv-btn-primary" onClick={this.handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2" />
                Adding...
              </>
            ) : 'Add Craft'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}
